export interface ListingConnection<T> {
  nextPage?: string;
  nodes: Array<T>;
  totalCount: number;
}

export interface Paginate {
  after?: string;
  limit?: number;
  offset?: number;
}

// Matches https://github.com/referenceable/referenceable_api/tree/develop/app/graphql/application/types/sort_enum_type.rb
export const SortDirectionEnum = {
  Ascending: 'asc',
  Descending: 'desc',
} as const;

export type SortDirection =
  (typeof SortDirectionEnum)[keyof typeof SortDirectionEnum];

export interface Searchable {
  search?: string;
}

export interface Sortable {
  sortBy?: string;
  sortDirection?: SortDirection;
}

export type ListingInput = Paginate & Searchable & Sortable;

type GenericListingRecord<T> = T & { id: string };

/**
 * Merges multiple listing connections into a single connection, maintaining
 * only unique nodes (based on their ID) and the total count of the first
 * connection that is given.
 *
 * @see https://gitlab.com/mindtickle/dsr/dsr-client/-/blob/track/integration/src/graphql/User/UserQueries.ts
 * @example Assuming the following GraphQL query:
 *
 *  query foo ($arg1: String!, $arg2: String) {
 *    alias1: foo (arg1: $arg1) {
 *      totalCount
 *      nodes {
 *        id
 *      }
 *    }
 *    alias2: foo (arg2: $arg2) {
 *      totalCount
 *      nodes {
 *        id
 *      }
 *    }
 *  }
 *
 * The result would be the equivalent of:
 *  {
 *    nodes: [...alias1.nodes, ...alias2.nodes],
 *    totalCount: alias1.totalCount
 *  }
 */
export const mergeListingConnectionAliases = <T>(
  ...connections: ListingConnection<GenericListingRecord<T>>[]
): ListingConnection<T> => {
  const uniqueNodes = new Map<string, GenericListingRecord<T>>();
  const totalCount = connections[0]?.totalCount ?? 0;

  connections.filter(Boolean).forEach((connection) => {
    connection.nodes.forEach((node) => {
      uniqueNodes.set(node.id, node);
    });
  });

  return {
    nodes: Array.from(uniqueNodes.values()),
    totalCount,
  };
};
